import React from 'react';

import { useIntl } from 'react-intl';

import { useDeletePayStation } from 'src/features/payment/api/useDeletePayStation';
import { LIST_PAY_STATIONS_RESOURCE } from 'src/features/payment/api/useListPayStations';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStation } from 'src/features/payment/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface DeletePayStationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation: PayStation;
    children?: React.ReactNode;
}

function DeletePayStationFormModal<TData>({ selectedPayStation, children }: DeletePayStationFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { mutate: deletePayStation } = useDeletePayStation({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATIONS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(paymentsMessages.deletePayStationSuccess),
                description: formatMessage(paymentsMessages.deletePayStationSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: paymentsMessages.deletePayStation,
                subTitle: paymentsMessages.deletePayStationSubtitle,
                icon: ICON_MAP.DELETE,
                confirmationMessage: paymentsMessages.confirmDeletePayStation,
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: paymentsMessages.deletePayStation,
                callbackFn: () => {
                    deletePayStation({
                        parkingId,
                        payStationId: selectedPayStation.id,
                    });
                    setIsModalOpen(false);
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeletePayStationFormModal;
